// =============================================================================
// Step 4 — Status page (personal dashboard)
// =============================================================================
// Per BRD §7 (open) + F6: personal map marker (dot/flag), the national goal,
// people-who-joined list, invite link still copyable, stage badge.
// The live national headcount is intentionally absent — only the 150,000 target
// and the supporter's OWN referral numbers are shown.
//
// In V1 the map will be a placeholder card — full Leaflet integration with
// the IsraelMap port comes in a follow-up commit. The structural wiring
// (data fetch + render) lands now.
// =============================================================================

import { useEffect, useState } from "react"
import { useClerk } from "@clerk/clerk-react"
import { Button } from "@/components/ui/button"
import { PageShell } from "@/components/brand/PageShell"
import { StepIndicator } from "@/components/brand/StepIndicator"
import { IsraelMap } from "@/components/brand/IsraelMap"
import { useApi } from "@/lib/api"
import { formatNumber } from "@/lib/utils"
import { Flag, Users, LogOut, Copy, Share2, Check } from "lucide-react"

interface Invitee {
  first_name: string
  invite_slug: string
  stage: string
  created_at: string
  city_name_he: string | null
  city_name_en: string | null
}

interface StatusResponse {
  supporter: {
    id: string
    first_name: string
    invite_slug: string
    stage: "dot" | "flag" | "flag_t2" | "flag_t3"
    direct_invitees_count: number
    total_descendants_count: number
  }
  invitees: Invitee[]
  // Target only — the national headcount is no longer served or shown.
  counter: { target: number }
  email: string
}

const STAGE_LABELS: Record<StatusResponse["supporter"]["stage"], string> = {
  dot: "נקודה על המפה",
  flag: "דגל",
  flag_t2: "דגל זוהר",
  flag_t3: "דגל הילה",
}

export function StatusPage() {
  const { signOut } = useClerk()
  const apiCall = useApi()
  const [data, setData] = useState<StatusResponse | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)

  useEffect(() => {
    apiCall<StatusResponse>("/api/me/status")
      .then(setData)
      .catch((e) => setError(e instanceof Error ? e.message : "שגיאה"))
  }, [apiCall])

  if (error)
    return (
      <PageShell>
        <div className="max-w-md mx-auto px-6 py-20 text-center text-sm text-[#6B7280]">
          {error}
        </div>
      </PageShell>
    )
  if (!data) {
    return (
      <PageShell bg="subtle">
        <div className="max-w-md mx-auto px-6 py-20 text-center text-sm text-[#6B7280]">
          טוען…
        </div>
      </PageShell>
    )
  }

  const { supporter, invitees, counter } = data
  const inviteUrl = `${window.location.origin}/i/${supporter.invite_slug}`
  // Display without the scheme so it fits one line; copy/share still use the full URL.
  const inviteDisplay = inviteUrl.replace(/^https?:\/\//, "")

  async function onCopy() {
    await navigator.clipboard.writeText(inviteUrl)
    setCopied(true)
    setTimeout(() => setCopied(false), 1800)
  }
  async function onShare() {
    if (navigator.share) {
      try {
        await navigator.share({
          title: "אל הדגל — פרויקט 150,000",
          text: "הצטרפו אליי — סרטון של דקה אחת, ואז דגל על המפה.",
          url: inviteUrl,
        })
      } catch {
        // user cancelled the native share sheet
      }
    } else {
      await onCopy()
    }
  }

  return (
    <PageShell bg="subtle" header={<StepIndicator current={3} total={3} />}>
      <main className="max-w-5xl mx-auto px-6 lg:px-10 py-8">
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <div>
            <h1 className="text-2xl lg:text-3xl font-extrabold text-[#0F1B36] leading-tight">
              שלום, {supporter.first_name}
            </h1>
            <p className="text-sm text-[#4B5563] mt-0.5">הסטטוס שלך מתעדכן בזמן אמת.</p>
          </div>
          <div className="flex items-center gap-3">
            <div className="inline-flex items-center gap-2 rounded-full bg-[#1B2D52] text-white px-4 py-1.5 text-sm font-semibold">
              <Flag className="size-4" />
              {STAGE_LABELS[supporter.stage]}
            </div>
            <button
              type="button"
              onClick={() => void signOut({ redirectUrl: "/" })}
              className="inline-flex items-center gap-1.5 rounded-full border border-[#1B2D52]/15 bg-white px-3 py-1.5 text-xs font-semibold text-[#6B7280] transition hover:text-[#1B2D52] hover:border-[#1B2D52]/30"
            >
              <LogOut className="size-3.5" />
              התנתקות
            </button>
          </div>
        </div>

        {/* Your personal invite link — the top box */}
        <div className="mb-4 rounded-2xl border border-[#1B2D52]/15 bg-white shadow-xl shadow-[#1B2D52]/5 p-6">
          <div className="text-xs font-semibold text-[#1B2D52] mb-1">הקישור האישי שלך</div>
          <p className="text-sm text-[#4B5563] mb-4">
            שתפ/י אותו — כל מי שמצטרף דרכו מוסיף דגל למפה ומופיע אצלך למטה.
          </p>
          <div
            dir="ltr"
            className="mb-3 w-full select-all overflow-x-auto whitespace-nowrap rounded-lg border border-[#E5E7EB] bg-[#F8FAFC] px-3 py-3 text-center text-xs font-mono text-[#1B2D52]"
          >
            {inviteDisplay}
          </div>
          <div className="flex items-center gap-2">
            <Button size="lg" variant="outline" onClick={onCopy} className="flex-1">
              {copied ? <Check className="size-4" /> : <Copy className="size-4" />}
              {copied ? "הועתק" : "העתק"}
            </Button>
            <Button size="lg" onClick={onShare} className="flex-1">
              <Share2 className="size-4" />
              שתפו
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* National goal — the target only. The live national headcount is
              deliberately not shown anywhere on the site. */}
          <div className="rounded-2xl border border-[#1B2D52]/15 bg-white p-5 shadow-sm">
            <div className="text-[10px] font-semibold uppercase tracking-wide text-[#6B7280]">
              היעד שלנו
            </div>
            <div className="mt-2 flex items-baseline gap-2" dir="ltr">
              <span className="text-3xl font-extrabold text-[#1B2D52] tabular-nums">
                {formatNumber(counter.target)}
              </span>
              <span className="text-base font-semibold text-[#6B7280]">תומכים</span>
            </div>
            <p className="mt-2 text-xs text-[#6B7280]">
              זה מה שצריך כדי להיכנס לכנסת. כל דגל חדש מקרב אותנו.
            </p>
          </div>

          {/* Joined through you — the whole chain (same design as the goal card) */}
          <div className="rounded-2xl border border-[#1B2D52]/15 bg-white p-5 shadow-sm">
            <div className="text-[10px] font-semibold uppercase tracking-wide text-[#6B7280]">
              הצטרפו דרכך
            </div>
            <div className="mt-2 flex items-baseline gap-2" dir="ltr">
              <span className="text-3xl font-extrabold text-[#1B2D52] tabular-nums">
                {formatNumber(supporter.total_descendants_count)}
              </span>
              <span className="text-base font-semibold text-[#6B7280]">אנשים</span>
            </div>
            <p className="mt-2 text-xs text-[#6B7280]">
              כולם — הצטרפו דרכך ודרך החברים שהם הביאו.
            </p>
          </div>
        </div>

        {/* Map placeholder + invitees list */}
        <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2 rounded-2xl border border-[#1B2D52]/15 bg-white p-5 shadow-sm min-h-[420px] flex flex-col">
            <div className="text-sm font-semibold text-[#1B2D52] mb-1">מפת התומכים</div>
            <p className="text-[11px] text-[#6B7280] mb-3">כל נקודה — תומכ/ה שהצטרף/ה לתנועה</p>
            <div className="flex-1 rounded-xl bg-[#F8FAFC] border border-[#E5E7EB] overflow-hidden min-h-[360px]">
              <IsraelMap />
            </div>
          </div>

          <div className="rounded-2xl border border-[#1B2D52]/15 bg-white p-5 shadow-sm">
            <div className="flex items-center gap-2 mb-3">
              <Users className="size-4 text-[#1B2D52]" />
              <span className="text-sm font-semibold text-[#1B2D52]">
                החברים שהצטרפו דרכך
              </span>
            </div>
            {invitees.length === 0 ? (
              <p className="text-sm text-[#6B7280]">
                אין עדיין. שתפ/י את הקישור — וזה ייפתח.
              </p>
            ) : (
              <ul className="space-y-2">
                {invitees.map((inv) => (
                  <li key={inv.invite_slug} className="flex items-center gap-2.5 text-sm">
                    <div className="size-8 rounded-full bg-[#1B2D52]/10 flex items-center justify-center text-xs font-bold text-[#1B2D52]">
                      {inv.first_name.slice(0, 1)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-[#0F1B36] font-medium truncate">
                        {inv.first_name}
                      </div>
                      <div className="text-xs text-[#6B7280] truncate">
                        {inv.city_name_he ?? "—"}
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

      </main>
    </PageShell>
  )
}
